import * as pdfjsLib from 'pdfjs-dist';
import { storageService } from './storageService';

// Use CDN worker to avoid .mjs MIME type issues on servers without explicit mjs config
pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdn.jsdelivr.net/npm/pdfjs-dist@${pdfjsLib.version}/build/pdf.worker.min.mjs`;

export const pdfProcessingService = {
  // Convert PDF file to individual page images and upload to storage
  async processPDFToPages(file, companyId, quality = 'standard') {
    try {
      const arrayBuffer = await file?.arrayBuffer();
      const pdf = await pdfjsLib?.getDocument({ data: arrayBuffer })?.promise;
      const pages = [];

      // Process each page
      for (let i = 1; i <= pdf?.numPages; i++) {
        const page = await pdf?.getPage(i);
        const scale = quality === 'high' ? 2.0 : 1.5;
        const viewport = page?.getViewport({ scale });

        // Render page to canvas
        const canvas = document.createElement('canvas');
        const context = canvas?.getContext('2d');
        canvas.height = viewport?.height;
        canvas.width = viewport?.width;

        await page?.render({
          canvasContext: context,
          viewport: viewport
        })?.promise;

        // Convert canvas to blob
        const blob = await new Promise((resolve) => {
          canvas?.toBlob(resolve, 'image/jpeg', quality === 'high' ? 0.95 : 0.85);
        });

        // Upload page image to storage
        const pageFile = new File([blob], `page-${i}.jpg`, { type: 'image/jpeg' });
        const { path, fullPath } = await storageService?.uploadCatalogFile(pageFile, companyId);

        // Store the path, not the signed URL (we'll generate fresh URLs when loading)
        const imagePath = path;

        // Create thumbnail (smaller version)
        const thumbnailCanvas = document.createElement('canvas');
        const thumbnailScale = 0.6;
        const thumbnailViewport = page?.getViewport({ scale: thumbnailScale });
        thumbnailCanvas.width = thumbnailViewport?.width;
        thumbnailCanvas.height = thumbnailViewport?.height;
        const thumbnailContext = thumbnailCanvas?.getContext('2d');

        await page?.render({
          canvasContext: thumbnailContext,
          viewport: thumbnailViewport
        })?.promise;

        const thumbnailBlob = await new Promise((resolve) => {
          thumbnailCanvas?.toBlob(resolve, 'image/jpeg', 0.7);
        });

        const thumbnailFile = new File([thumbnailBlob], `thumb-${i}.jpg`, { type: 'image/jpeg' });
        const thumbnailResult = await storageService?.uploadThumbnail(thumbnailFile);

        pages?.push({
          pageNumber: i,
          imagePath, // Store path instead of URL
          imageUrl: null, // Will be generated on load
          thumbnailUrl: thumbnailResult?.publicUrl,
          width: Math.round(viewport?.width),
          height: Math.round(viewport?.height),
          imageAlt: `Page ${i}`,
          thumbnailAlt: `Thumbnail for page ${i}`
        });
      }

      return pages;
    } catch (error) {
      console.error('Error processing PDF:', error);
      throw new Error('Failed to process PDF. Please ensure the file is not corrupted or password-protected.');
    }
  },

  // Validate PDF file
  validatePDFFile(file) {
    const maxSize = 50 * 1024 * 1024; // 50MB
    const validTypes = ['application/pdf'];

    if (!file) {
      throw new Error('No file provided');
    }

    if (!validTypes?.includes(file?.type)) {
      throw new Error('Invalid file type. Only PDF files are supported.');
    }

    if (file?.size > maxSize) {
      throw new Error('File size exceeds 50MB limit.');
    }

    return true;
  },

  // Extract PDF metadata
  async extractPDFMetadata(file) {
    try {
      const arrayBuffer = await file?.arrayBuffer();
      const pdf = await pdfjsLib?.getDocument({ data: arrayBuffer })?.promise;
      const metadata = await pdf?.getMetadata();

      return {
        numPages: pdf?.numPages,
        title: metadata?.info?.Title || file?.name?.replace('.pdf', ''),author: metadata?.info?.Author || '',subject: metadata?.info?.Subject || '',keywords: metadata?.info?.Keywords || '',creator: metadata?.info?.Creator || '',producer: metadata?.info?.Producer || '',
        creationDate: metadata?.info?.CreationDate || null
      };
    } catch (error) {
      console.error('Error extracting PDF metadata:', error);
      return {
        numPages: 0,
        title: file?.name?.replace('.pdf', '')
      };
    }
  }
};